<template>

  <v-card ref="appSign" class="sign-component elevation-0" :class="[isMobileOnly ? 'mobile' : '']">

    <v-card-text style="height: 100%" class="pa-0">

      <div v-if="isMobileOnly && isStampAllowed" class="pb-1 d-flex align-center justify-space-between">

        <v-chip-group v-model="selectedTab" active-class="primary--text" mandatory @change="onTabChange">
          <v-chip :value="AppSignTab.Sign">
            {{$t(`appSign.keySelector.${PrivateKeyType[PrivateKeyType.Key]}`)}}
            <v-icon v-if="isSignKeyReady" small color="success" class="ml-1">far fa-check-circle</v-icon>
          </v-chip>

          <v-chip v-if="isSealAdded" :value="AppSignTab.Seal" class="ml-1" close @click:close="onSealRemoveClicked">
            {{$t(`appSign.keySelector.${PrivateKeyType[PrivateKeyType.Seal]}`)}}
            <v-icon v-if="isSealKeyReady" small color="success" class="ml-1 mr-2">far fa-check-circle</v-icon>
            <v-icon v-if="!isSealKeyReady && signSettings.isStampRequired" small color="warning" class="ml-1 mr-2">
              fas fa-exclamation-triangle
            </v-icon>
          </v-chip>

        </v-chip-group>

        <v-btn v-if="!isSealAdded" small color="primary" outlined @click.stop="onSealAddClicked">
          <v-icon class="mr-1" dark>fas fa-plus-circle</v-icon>
          {{ $t('appSign.addSeal') }}
        </v-btn>

      </div>

      <v-tabs-items v-model="visibleTabs" multiple :class="isMobileOnly ? '' : 'app-sign-main-tabs-items'">

        <v-tab-item v-show="visibleTabs.includes(AppSignTab.Sign)" :class="selectedTab === AppSignTab.Sign ? 'active-tab' : ''"
          @click.native="onKeySelectorClicked(AppSignTab.Sign, $event)">

          <app-sign-key-selector v-if="isSignKeySelectorRendered" :eventBus="signKeyBus" :privateKeyType="PrivateKeyType.Key"
            :active="selectedTab === AppSignTab.Sign" :show-title="!isMobileOnly && isStampAllowed" :should-validate-key-type="isSealAdded"
            @readed="onSignKeyReaded" @ready="setSignKeyReadiness(true)" @not-ready="setSignKeyReadiness(false)">
            <template v-slot:title-append>
              <v-btn v-if="!isSealAdded" small color="primary" outlined @click.stop="onSealAddClicked">
                <v-icon class="mr-1" dark>fas fa-plus-circle</v-icon>
                {{ $t('appSign.addSeal') }}
              </v-btn>
            </template>
          </app-sign-key-selector>

        </v-tab-item>

        <v-tab-item v-show="isSealAdded && visibleTabs.includes(AppSignTab.Seal)"
          :class="selectedTab === AppSignTab.Seal ? 'active-tab' : ''" @click.native="onKeySelectorClicked(AppSignTab.Seal, $event)">

          <app-sign-key-selector v-if="isSealKeySelectorRendered" :eventBus="sealKeyBus" :privateKeyType="PrivateKeyType.Seal"
            :allowedMethods="sealKeySelectorAllowedMethods" :active="selectedTab === AppSignTab.Seal"
            :show-title="!isMobileOnly && isStampAllowed" :should-validate-key-type="true" @readed="onSealKeyReaded"
            @ready="setSealKeyReadiness(true)" @not-ready="setSealKeyReadiness(false)">
            <template v-slot:title-append>
              <v-btn v-if="isSealAdded" small color="warning" outlined @click.stop="onSealRemoveClicked">
                <v-icon class="mr-1" dark>fa-trash-alt</v-icon>
                {{ $t('appSign.removeSeal') }}
              </v-btn>
            </template>
          </app-sign-key-selector>

        </v-tab-item>

      </v-tabs-items>

      <div v-if="messageText" v-html="messageText" :class="[ 'info-panel pa-2 mt-2', messageIsError ? 'error': 'success' ]">
      </div>

      <div v-if="signSettings.isShowDocumentFiles" class="mt-2">
        {{ $t('appSign.filesBlock') + ' ' + (fileNames || $t('appSign.allFilesBlock')) }}
      </div>

    </v-card-text>

    <v-card-actions v-if="showButtons" class="px-0 mt-1">
      <v-row no-gutters>
        <v-col cols="12 d-flex">
          <v-btn id="appSignClearKeyBtn" v-if="isResetBtnVisible" :disabled="isResetBtnDisabled" color="secondary" @click="clearKey">
            {{$t('buttons.removesign')}}
          </v-btn>
          <v-spacer></v-spacer>
          <slot name="prepend-submit-btn"></slot>
          <v-btn id="appSignSignBtn" :disabled="isSignBtnDisabled" color="primary" @click="sign">
            {{ $t(signButtonText) }}
          </v-btn>
        </v-col>
      </v-row>
    </v-card-actions>

  </v-card>

</template>

<script>
import Vue from 'vue';

import { formatErrorToHtml } from '@/helpers/';
import uiMixin from '@/mixins/ui-mixin';

import AppSignKeySelector from './AppSignKeySelector';

import { signService } from '@/sign/services/sign.service';
import { SignSettings } from '@/sign/models/sign-settings.model';
import { PrivateKeyType, SignMethod, AppSignTab } from '@/sign/models/sign-constants';

import { logger } from '@/helpers/log.service';
import { AppError } from '@/models/log.models';
import { SigningParamsContainer } from '../models/signing-params.model';
import { euSignService } from '@/sign/services/eusign.service';

export default {
  name: 'AppSign',
  components: {
    AppSignKeySelector
  },
  mixins: [uiMixin],
  inject: ['isMobileOnly'],
  props: {
    eventBus: { default: () => new Vue() },
    showButtons: { default: false }
  },
  data() {
    return {
      fileNames: '',
      messageIsError: false,
      messageText: '',
      renderedTabs: [AppSignTab.Sign],
      selectedTab: AppSignTab.Sign,
      visibleTabs: [AppSignTab.Sign],
      signSettings: new SignSettings(),
      signButtonText: '',
      enableSignButton: false,
      isSignKeyReaded: false,
      isSealKeyReaded: false,
      isSignKeyReady: false,
      isSealKeyReady: false,
      isSignKeyResetBtnVisible: true,
      isSealKeyResetBtnVisible: true,
      signKeyBus: new Vue(),
      sealKeyBus: new Vue(),
      sealKeySelectorAllowedMethods: [SignMethod.FileSystem, SignMethod.Device, SignMethod.Cloud],
      isSealAdded: false,
      isSigningInProgress: false
    };
  },
  computed: {
    isSignKeySelectorRendered() {
      return this.renderedTabs.includes(AppSignTab.Sign);
    },
    isSealKeySelectorRendered() {
      return this.renderedTabs.includes(AppSignTab.Seal);
    },
    isStampAllowed() {
      return this.signSettings && this.signSettings.isStampAllowed;
    },
    isSignKeySelectorTabSelected() {
      return this.isSignKeySelectorTab(this.selectedTab);
    },
    isSealKeySelectorTabSelected() {
      return this.isSealKeySelectorTab(this.selectedTab);
    },
    PrivateKeyType() {
      return PrivateKeyType;
    },
    isResetBtnDisabled() {
      return this.isMobileOnly
        ? (this.isSignKeySelectorTabSelected && !this.isSignKeyReaded) || (this.isSealKeySelectorTabSelected && !this.isSealKeyReaded)
        : !this.isSignKeyReaded && !this.isSealKeyReaded;
    },
    isSignBtnDisabled() {
      return this.isSigningInProgress;
    },
    isResetBtnVisible() {
      return this.isMobileOnly
        ? (this.isSignKeySelectorTabSelected && this.isSignKeyResetBtnVisible) ||
            (this.isSealKeySelectorTabSelected && this.isSealKeyResetBtnVisible)
        : this.isSignKeyResetBtnVisible || (this.isSealAdded && this.isSealKeyResetBtnVisible);
    },
    signKeySigningParamsRequestMsg() {
      return `SigningParams-Request-${PrivateKeyType.Key}`;
    },
    signKeySigningParamsResponseMsg() {
      return `SigningParams-Response-${PrivateKeyType.Key}`;
    },
    sealKeySigningParamsRequestMsg() {
      return `SigningParams-Request-${PrivateKeyType.Seal}`;
    },
    sealKeySigningParamsResponseMsg() {
      return `SigningParams-Response-${PrivateKeyType.Seal}`;
    },
    AppSignTab() {
      return AppSignTab;
    }
  },
  mounted() {
    this.subscribe();
    this.prepareComponent();
  },
  beforeDestroy() {
    this.unsubscribe();
  },
  methods: {
    applySignSettings() {
      this.signSettings = signService.getCurrentSettings();
      this.fileNames = this.signSettings.fileNames;
      this.signButtonText = this.signSettings.submitBtnText || 'buttons.sign';
    },
    onTabChange(tab) {
      if (!this.renderedTabs.includes(tab)) this.renderedTabs.push(tab);
      this.visibleTabs = [tab];
      this.sendButtonsSettingsChanged();
    },
    handleError(error, header) {
      if (error instanceof AppError === false) {
        logger.error(error);
      }
      this.messageIsError = true;
      this.messageText = formatErrorToHtml(error, header);
      this.$nextTick(() => this.$refs.appSign.$el.scrollIntoView(false));
    },
    async reInitializeIfRequired() {
      return new Promise((resolve) => {
        if (euSignService.isReinitializationRequired()) {
          logger.debug(`[${this.$options.name}] reinitializing component ...`);

          this.renderedTabs.splice(0);
          this.visibleTabs.splice(0);

          euSignService.reInit();

          this.$nextTick(() => {
            this.renderedTabs.push(AppSignTab.Sign);
            this.visibleTabs.push(AppSignTab.Sign);

            this.$nextTick(() => {
              logger.debug(`[${this.$options.name}] component's been reinitialized successfully`);
              resolve();
            });
          });
        } else {
          resolve();
        }
      });
    },
    async prepareComponent() {
      await this.reInitializeIfRequired();

      this.applySignSettings();

      if (this.signSettings.resetReadedKeys) {
        this.resetSignKey();
        this.resetSealKey();
      }

      if (this.isStampAllowed) {
        if (this.signSettings.isStampRequired) {
          this.isSealAdded = true;
          if (!this.isMobileOnly) {
            if (!this.renderedTabs.includes(AppSignTab.Seal)) this.renderedTabs.push(AppSignTab.Seal);
            if (!this.visibleTabs.includes(AppSignTab.Seal)) this.visibleTabs.push(AppSignTab.Seal);
            this.sendKeySelectorUiUpdated();
          }
        }
      } else {
        this.isSealAdded = false;
        const sealIdx = this.visibleTabs.findIndex((el) => el === AppSignTab.Seal);
        if (sealIdx > -1) {
          this.visibleTabs.splice(sealIdx, 1);
          this.sendKeySelectorUiUpdated();
          if (this.selectedTab !== AppSignTab.Sign) {
            this.selectedTab = AppSignTab.Sign;
            this.onTabChange(this.selectedTab);
          }
        }
      }

      this.signKeyBus.$emit('app-sign-prepare');
      if (this.isSealAdded) {
        this.sealKeyBus.$emit('app-sign-prepare');
      }
    },
    clearKey() {
      if (this.isMobileOnly) {
        if (this.isSignKeySelectorTabSelected) {
          this.resetSignKey();
        } else {
          this.resetSealKey();
        }
      } else {
        this.resetSignKey();
        this.resetSealKey();
      }

      this.resetMessage();
    },
    resetSignKey() {
      this.isSignKeyReaded = false;
      this.isSignKeyReady = false;
      this.signKeyBus.$emit('app-sign-reset-private-key');
      this.sendButtonsSettingsChanged();
    },
    resetSealKey() {
      this.isSealKeyReaded = false;
      this.isSealKeyReady = false;
      this.sealKeyBus.$emit('app-sign-reset-private-key');
      this.sendButtonsSettingsChanged();
    },
    resetMessage() {
      this.messageText = '';
      this.messageIsError = false;
    },
    async sign() {
      try {
        this.isSigningInProgress = true;
        this.sendButtonsSettingsChanged();

        let signPrms = new SigningParamsContainer();
        signPrms[PrivateKeyType.Key] = await this.getSignKeySigningParams();

        if (!signPrms[PrivateKeyType.Key]) return;

        if (this.isStampAllowed && this.isSealAdded && this.isSealKeySelectorRendered) {
          await this.hideLoading();
          let sealSigningParams = await this.getSealKeySigningParams();
          if (sealSigningParams) {
            sealSigningParams.validateKeyType = true;
            signPrms[PrivateKeyType.Seal] = sealSigningParams;
          } else {
            return;
          }
        }

        if (signPrms[PrivateKeyType.Seal]) {
          signPrms[PrivateKeyType.Key].validateKeyType = true;
        }

        logger.debug(`[AppSign:sign] signPrms`, signPrms);

        if (!signPrms[PrivateKeyType.Key]) return;

        if (this.signSettings.isConfirmRequired && !(await this.showConfirm(this.signSettings.confirmText || 'appSign.siqnQuestion'))) {
          return;
        }

        if (!(await this.validateSigningParams(signPrms))) return;

        this.resetMessage();

        await this.hideLoading();

        this.showLoading({}, true);

        const signResult = await signService.sign(signPrms);
        logger.debug(`[AppSign:sign] sign result`, signResult);

        if (signResult.isSucceed) {
          this.$emit('signed', signResult.result);
        } else if (signResult.isError) {
          let header = 'appSign.siqnError';
          if (signResult.errorSource && (signResult.errorSource === PrivateKeyType.Key || signResult.errorSource === PrivateKeyType.Seal)) {
            header += signResult.errorSource;
          }
          this.handleError(signResult.error, header);
          this.$emit('signError', signResult.error);
        }
      } catch (error) {
        logger.error(error);
      } finally {
        await this.hideLoading();
        this.isSigningInProgress = false;
        this.sendButtonsSettingsChanged();
      }
    },
    subscribe() {
      this.$eventBus.$on('user-logged-out', this.resetForm);
      this.$eventBus.$on('app-sign-reset-private-key', this.onGetResetEventFromGlobalBus);
      this.subscribeOnParentEvents();
      this.subscribeOnSignKeyEvents();
      this.subscribeOnSealKeyEvents();
    },
    unsubscribe() {
      this.$eventBus.$off('user-logged-out', this.resetForm);
      this.$eventBus.$off('app-sign-reset-private-key', this.onGetResetEventFromGlobalBus);
      this.unsubscribeOffParentEvents();
      this.unsubscribeOffSignKeyEvents();
      this.unsubscribeOffSealKeyEvents();
    },
    onSignKeyReaded() {
      this.isSignKeyReaded = true;
      this.sendButtonsSettingsChanged();
    },
    onSealKeyReaded() {
      this.isSealKeyReaded = true;
      this.sendButtonsSettingsChanged();
    },
    setSignKeyReadiness(isReady) {
      this.isSignKeyReady = isReady;
      this.sendButtonsSettingsChanged();
    },
    setSealKeyReadiness(isReady) {
      this.isSealKeyReady = isReady;
      this.sendButtonsSettingsChanged();
    },
    getSignKeySigningParams() {
      return new Promise((resolve) => {
        const fn = (prms) => {
          this.signKeyBus.$off(this.signKeySigningParamsResponseMsg);
          resolve(prms);
        };
        this.signKeyBus.$on(this.signKeySigningParamsResponseMsg, fn);
        this.signKeyBus.$emit(this.signKeySigningParamsRequestMsg);
      });
    },
    getSealKeySigningParams() {
      return new Promise((resolve) => {
        const fn = (prms) => {
          this.sealKeyBus.$off(this.sealKeySigningParamsResponseMsg);
          resolve(prms);
        };
        this.sealKeyBus.$on(this.sealKeySigningParamsResponseMsg, fn);
        this.sealKeyBus.$emit(this.sealKeySigningParamsRequestMsg);
      });
    },

    subscribeOnParentEvents() {
      this.eventBus.$on('app-sign-prepare', this.prepareComponent);
      this.eventBus.$on('app-sign-reset-private-key', this.clearKey);
      this.eventBus.$on('app-sign-execute-sign', this.sign);
    },
    subscribeOnSignKeyEvents() {
      this.signKeyBus.$on('app-sign-reset-private-key-from-child', this.resetSignKey);
      this.signKeyBus.$on('app-sign-change-ui-settings', this.onSignKeyUiSettingsChanged);
    },
    subscribeOnSealKeyEvents() {
      this.sealKeyBus.$on('app-sign-reset-private-key-from-child', this.resetSealKey);
      this.sealKeyBus.$on('app-sign-change-ui-settings', this.onSealUiSettingsChanged);
    },
    unsubscribeOffParentEvents() {
      this.eventBus.$off('app-sign-prepare', this.prepareComponent);
      this.eventBus.$off('app-sign-reset-private-key', this.clearKey);
      this.eventBus.$off('app-sign-execute-sign', this.sign);
    },
    unsubscribeOffSignKeyEvents() {
      this.signKeyBus.$off('app-sign-reset-private-key-from-child', this.resetSignKey);
      this.signKeyBus.$off('app-sign-change-ui-settings', this.onSignKeyUiSettingsChanged);
    },
    unsubscribeOffSealKeyEvents() {
      this.sealKeyBus.$off('app-sign-reset-private-key-from-child', this.resetSealKey);
      this.sealKeyBus.$off('app-sign-change-ui-settings', this.onSealUiSettingsChanged);
    },
    onSignKeyUiSettingsChanged(s) {
      this.isSignKeyResetBtnVisible = s.isResetBtnVisible;
      this.sendButtonsSettingsChanged();
    },
    onSealUiSettingsChanged(s) {
      this.isSealKeyResetBtnVisible = s.isResetBtnVisible;
      this.sendButtonsSettingsChanged();
    },
    sendButtonsSettingsChanged() {
      this.$emit('buttons-settings-changed', {
        isResetBtnVisible: this.isResetBtnVisible,
        isResetBtnDisabled: this.isResetBtnDisabled,
        isSignBtnDisabled: this.isSignBtnDisabled,
        isSealAdded: this.isSealAdded
      });
    },
    isSignKeySelectorTab(tab) {
      return tab === AppSignTab.Sign;
    },
    isSealKeySelectorTab(tab) {
      return tab === AppSignTab.Seal;
    },
    resetForm() {
      this.resetSignKey();
      this.resetSealKey();
      this.resetMessage();
    },
    onGetResetEventFromGlobalBus(prms) {
      if (!prms || !prms.privateKeyType || prms.privateKeyType === PrivateKeyType.Key) {
        this.resetSignKey();
      }
      if (!prms || !prms.privateKeyType || prms.privateKeyType === PrivateKeyType.Seal) {
        this.resetSealKey();
      }
    },
    async validateSigningParams(signPrms) {
      let result = true;
      if (this.signSettings.isStampRequired && (!signPrms || !signPrms[PrivateKeyType.Seal])) {
        // prettier-ignore
        let chosingSealConfirmed = await this.showConfirm(
          'appSign.signWithoutRequriedStampWarning',
          'appSign.signWithoutRequriedStampWarningTitle', false, '', {},
          'appSign.signWithoutRequriedStamp', 'appSign.selectStamp', 'warning', 'primary');
        result = chosingSealConfirmed;
      }
      return result;
    },
    onKeySelectorClicked(tab, e) {
      if (!this.isMobileOnly && this.selectedTab !== tab) {
        this.selectedTab = tab;
        this.sendButtonsSettingsChanged();
      }
    },
    onSealAddClicked() {
      this.isSealAdded = true;
      if (!this.isMobileOnly) {
        if (!this.renderedTabs.includes(AppSignTab.Seal)) this.renderedTabs.push(AppSignTab.Seal);
        if (!this.visibleTabs.includes(AppSignTab.Seal)) this.visibleTabs.push(AppSignTab.Seal);
        this.sendKeySelectorUiUpdated();
      }
      this.sealKeyBus.$emit('app-sign-prepare');
    },
    onSealRemoveClicked() {
      this.isSealAdded = false;
      const sealIdx = this.visibleTabs.findIndex((el) => el === AppSignTab.Seal);
      if (sealIdx > -1) {
        this.visibleTabs.splice(sealIdx, 1);
        this.sendKeySelectorUiUpdated();
      }
    },
    sendKeySelectorUiUpdated() {
      this.signKeyBus.$emit('app-sign-ui-updated');
      this.sealKeyBus.$emit('app-sign-ui-updated');
      this.sendButtonsSettingsChanged();
    }
  }
};
</script>

<style lang="scss">
@import '@/styles/variables.scss';
@import '@/sign/styles/sign.scss';

.sign-component {
  &:not(.mobile) {
    min-width: 500px;
  }
  .app-input-label {
    font-weight: normal;
  }
  #signMethodTab {
    .v-slide-group__prev {
      display: none;
    }
  }
  .app-sign-main-tabs-items > .v-window__container {
    display: flex;
    flex-direction: row;
    .v-window-item {
      border-radius: $app-defaults-block-border-radius;
      .v-tabs-items,
      .v-tabs-bar {
        background-color: transparent;
      }
      flex-shrink: 0;
      flex-grow: 1;
      flex-basis: 50%;
      overflow-x: hidden;
    }
    .v-window-item.active-tab {
      background-color: #f9f9f9;
    }
  }
}
</style>
